// src/components/dashboard/ElectricityYearsContextTable.js
import React, { useMemo } from "react";
import { useElectricityGenYearTotalTWh } from "../../hooks/useElectricityGenYearTotalTWh";
import { buildElectricityContexts } from "../../lib/electricityContexts";

/**
 * Table comparing a fixed 2025 baseline vs the selected scenario in 2050.
 *
 * Props:
 * - selectedScenario (string, required): user's chosen scenario key -> used at 2050
 * - baselineScenario (string, optional): scenario key for the 2025 baseline.
 *     If not provided, defaults to 'net_zero_futures_dvl_shift_high_0509'.
 *
 * (We ignore any "comparison" now.)
 */
export default function ElectricityYearsContextTable({
  selectedScenario,
  baselineScenario = "net_zero_futures_dvl_shift_high_0909",
}) {
  // Fetch totals (TWh) for the two exact points
  const base = useElectricityGenYearTotalTWh({
    scenario: baselineScenario,
    year: 2025,
  });

  const sel = useElectricityGenYearTotalTWh({
    scenario: selectedScenario,
    year: 2050,
  });
 console.log({selectedScenario, baselineScenario, base, sel});
  const loading = base.loading || sel.loading;
  const error = base.error || sel.error;

  // Build contexts
  const currentContexts = useMemo(
    () => buildElectricityContexts(base.totalTWh),
    [base.totalTWh]
  );
  const selectedContexts = useMemo(
    () => buildElectricityContexts(sel.totalTWh),
    [sel.totalTWh]
  );

  // helpers
  const safeRatio = (num, den) => {
    if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return 1;
    return Math.max(0.1, num / den);
    // clamp the icon count to at least 1/10th so UI never vanishes
  };
  const iconCount = (num, den) => Math.max(1, Math.round(safeRatio(num, den) * 10));
  const fmtInt  = (n) => Math.round(n).toLocaleString();
  const fmt1dp  = (n) =>
    (Math.round(n * 10) / 10).toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });

  if (loading) {
    return (
      <div className="text-xs text-gray-500">
        Loading electricity contexts (2025 baseline vs 2050 selected)…
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-xs text-red-600">
        Couldn’t load contexts: {error.message}
      </div>
    );
  }

  return (
    <table className="w-full">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Context
          </th>
          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
            2025
          </th>
          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
            Selected Scenario (2050)
          </th>
        </tr>
      </thead>

      <tbody className="bg-white">
        {/* Households */}
        <tr>
          <td className="px-4 py-6 align-top text-left w-1/4 max-w-xs">
            <div className="text-sm font-medium text-gray-900">
              {/* Every household in the UK's current electricity usage */}
            </div>
            <div className="text-sm text-gray-900 mt-1">
              How long could all UK households be powered if all the electricity generated in the stated year went only to them?
            </div>
          </td>

          {/* Baseline (2025) */}
          <td className="px-4 py-6 text-center bg-gray-50">
            <div className="flex justify-center gap-1 text-lg mb-3 flex-wrap max-w-20 mx-auto">
              {Array(10).fill("🏘️").map((icon, i) => <span key={`baseline-households-${i}`}>{icon}</span>)}
            </div>
            <div className="text-lg font-bold text-gray-600">
              {fmt1dp(currentContexts.ukHouseholdsYears)}
            </div>
            <div className="text-xs text-gray-500">years</div>
          </td>

          {/* Selected (2050) */}
          <td className="px-4 py-6 text-center bg-blue-50">
            <div className="flex justify-center gap-1 text-lg mb-3 flex-wrap max-w-20 mx-auto">
              {Array(iconCount(
                selectedContexts.ukHouseholdsYears,
                currentContexts.ukHouseholdsYears
              )).fill("🏘️").map((icon, i) => <span key={`selected-households-${i}`}>{icon}</span>)}
            </div>
            <div className="text-lg font-bold text-blue-600">
              {fmt1dp(selectedContexts.ukHouseholdsYears)}
            </div>
            <div className="text-xs text-blue-500">years</div>
          </td>
        </tr>

        {/* Toast */}
        <tr className="border-t border-gray-200">
          <td className="px-4 py-6 align-top text-left w-1/4 max-w-xs">
            <div className="text-sm font-medium text-gray-900">
            How many slices of toast per day could everyone in the UK make, if all the electricity generated in the stated year only went on making toast?
            </div>
          </td>

          <td className="px-4 py-6 text-center bg-gray-50">
            <div className="flex justify-center gap-1 text-lg mb-3 flex-wrap max-w-20 mx-auto">
              {Array(10).fill("🍞").map((icon, i) => <span key={`baseline-toast-${i}`}>{icon}</span>)}
            </div>
            <div className="text-lg font-bold text-gray-600">
              {fmtInt(currentContexts.toastSlicesPerPersonPerDay)}
            </div>
            <div className="text-xs text-gray-500">slices per person per day</div>
          </td>

          <td className="px-4 py-6 text-center bg-blue-50">
            <div className="flex justify-center gap-1 text-lg mb-3 flex-wrap max-w-20 mx-auto">
              {Array(iconCount(
                selectedContexts.toastSlicesPerPersonPerDay,
                currentContexts.toastSlicesPerPersonPerDay
              )).fill("🍞").map((icon, i) => <span key={`selected-toast-${i}`}>{icon}</span>)}
            </div>
            <div className="text-lg font-bold text-blue-600">
              {fmtInt(selectedContexts.toastSlicesPerPersonPerDay)}
            </div>
            <div className="text-xs text-blue-500">slices per day</div>
          </td>
        </tr>

        {/* Kettles */}
        <tr className="border-t border-gray-200">
          <td className="px-4 py-6 align-top text-left w-1/4 max-w-xs">
            <div className="text-sm font-medium text-gray-900">
            How many kettles could be boiled per day by everyone in the UK if all the electricity generated in the stated year only went on boiling kettles?
            </div>
          </td>

          <td className="px-4 py-6 text-center bg-gray-50">
            <div className="flex justify-center gap-1 text-lg mb-3 flex-wrap max-w-20 mx-auto">
              {Array(10).fill("🫖").map((icon, i) => <span key={`baseline-kettle-${i}`}>{icon}</span>)}
            </div>
            <div className="text-lg font-bold text-gray-600">
              {fmtInt(currentContexts.kettleBoilsPerPersonPerDay)}
            </div>
            <div className="text-xs text-gray-500">boils per person per day</div>
          </td>

          <td className="px-4 py-6 text-center bg-blue-50">
            <div className="flex justify-center gap-1 text-lg mb-3 flex-wrap max-w-20 mx-auto">
              {Array(iconCount(
                selectedContexts.kettleBoilsPerPersonPerDay,
                currentContexts.kettleBoilsPerPersonPerDay
              )).fill("🫖").map((icon, i) => <span key={`selected-kettle-${i}`}>{icon}</span>)}
            </div>
            <div className="text-lg font-bold text-blue-600">
              {fmtInt(selectedContexts.kettleBoilsPerPersonPerDay)}
            </div>
            <div className="text-xs text-blue-500">boils per day</div>
          </td>
        </tr>
      </tbody>
    </table>
  );
}
